import type { LocationLevelType } from "api-types/locations/locationLevelTypes";
import { getLocationIcon } from "lib/components/builders/getLocationIcon";
import { HOME_PLANS_NOT_ALLOWED_TOOLTIP, MULTI_UNIT_DISABLED_TOOLTIP } from "lib/constants/messages";
import type { TreeSelectNodeType } from "ui/components/TreeSelect";
import type { OptionProps } from "ui/forms/Select";

interface LocationSelectTreeInput {
  id: string;
  name: string;
  parentId?: string | null;
  archivedAt?: Date | null;
  locationLevel?: { type: LocationLevelType };
  allowHomePlans?: boolean;
}

interface BuildLocationSelectTreeOptions {
  selectedIds?: Set<string>;
  disableMultiUnit?: boolean;
  disableWhenHomePlansNotAllowed?: boolean;
}

function getDisabledTooltip(multiUnitDisabled: boolean, homePlansDisabled: boolean): string | undefined {
  if (multiUnitDisabled) {
    return MULTI_UNIT_DISABLED_TOOLTIP;
  }
  if (homePlansDisabled) {
    return HOME_PLANS_NOT_ALLOWED_TOOLTIP;
  }
  return undefined;
}

/**
 * Build a TreeSelect-friendly data structure from flat locations.
 * @param locations - Array of locations to build tree from
 * @param options - Optional configuration for pre-selection and disabling
 */
export function buildLocationSelectTree(
  locations: LocationSelectTreeInput[] | undefined,
  options?: Set<string> | BuildLocationSelectTreeOptions,
): TreeSelectNodeType[] {
  // Support legacy call signature: buildLocationSelectTree(locs, selectedIdsSet)
  const { selectedIds, disableMultiUnit, disableWhenHomePlansNotAllowed } =
    options instanceof Set
      ? { selectedIds: options, disableMultiUnit: false, disableWhenHomePlansNotAllowed: false }
      : options ?? {};

  if (!locations || locations.length === 0) {
    return [];
  }

  // Filter archived locations
  const visible = locations.filter((loc) => !loc.archivedAt);

  // First pass: create nodes and parent index
  const nodesById: Record<string, TreeSelectNodeType> = {};
  const parentIdById: Record<string, string | undefined> = {};
  for (const loc of visible) {
    const isMultiUnit = loc.locationLevel?.type === "multiunit";
    const multiUnitDisabled = !!disableMultiUnit && isMultiUnit;
    const homePlansDisabled = !!disableWhenHomePlansNotAllowed && loc.allowHomePlans === false;
    const disabled = multiUnitDisabled || homePlansDisabled;
    const disabledTooltip = getDisabledTooltip(multiUnitDisabled, homePlansDisabled);
    nodesById[loc.id] = {
      id: loc.id,
      name: loc.name,
      label: loc.name,
      children: [],
      selected: disabled ? false : selectedIds?.has(loc.id) ?? false,
      icon: getLocationIcon(loc.locationLevel?.type),
      descriptor: loc.locationLevel?.type,
      disabled,
      disabledTooltip,
    };
    parentIdById[loc.id] = loc.parentId ?? undefined;
  }

  // Second pass: link children to parents; collect roots
  const roots: TreeSelectNodeType[] = [];
  for (const loc of visible) {
    const node = nodesById[loc.id];
    const parentId = parentIdById[loc.id];
    if (parentId && nodesById[parentId]) {
      nodesById[parentId].children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortTree = (nodes: TreeSelectNodeType[]): void => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    nodes.forEach((n) => {
      if (n.children.length > 0) {
        sortTree(n.children);
      }
    });
  };
  sortTree(roots);

  return roots;
}

export function treeToSelectOptions(nodes: TreeSelectNodeType[], depth = 0): OptionProps[] {
  const options: OptionProps[] = [];
  nodes.forEach((node, index) => {
    if (depth === 0 && index > 0) {
      options.push({ value: `ruler-${node.id}`, label: "", displayBehavior: "ruler" });
    }
    options.push({
      value: node.id,
      label: node.label,
      icon: node.icon,
      indent: depth,
      ...(node.disabled ? { displayBehavior: "disabled", title: node.disabledTooltip } : {}),
    });
    if (node.children.length > 0) {
      options.push(...treeToSelectOptions(node.children, depth + 1));
    }
  });
  return options;
}
